// Partner-branded Drip pages.
//
// A partner page reuses the full Drip deposit/withdraw flow but locks the reward
// token to a single asset and re-skins the accent + hero. Everything else (input
// asset picker, position card, withdraw, FAQ) is shared with the base Drip page.

/** Glyph watermarked behind the hero by <PartnerBackground />. */
export type PartnerWatermark = "bull" | "shield";

export interface DripPartner {
  /** URL slug, e.g. "ansem" -> /ansem */
  slug: string;
  /** Reward token symbol shown throughout the UI. */
  tokenSymbol: string;
  /** Human-readable token name. */
  tokenName: string;
  /** SPL mint the deposit is locked to. */
  tokenMint: string;
  /**
   * Optional static icon override (served from /public). If the file is missing
   * or fails to load, the UI falls back to on-chain/Jupiter metadata, then to a
   * coloured monogram. Drop the partner logo here to force an exact asset.
   */
  iconUrl?: string;
  /** Solid accent colour (hex). Replaces the Kyros cyan on this page only. */
  accent: string;
  /** Same accent as "r, g, b" so we can build translucent rgba() surfaces. */
  accentRgb: string;
  /** Big hero heading (defaults to "Drip" if omitted). */
  heroTitle?: string;
  /** Header nav label (defaults to tokenSymbol if omitted). */
  navLabel?: string;
  /**
   * Brands the "Drip" nav item with this partner on EVERY route, rather than
   * only while the partner's own page is open. A route partner still wins on
   * its own page, so at most one partner ever brands the item.
   */
  pinnedInNav?: boolean;
  /** Which silhouette is watermarked behind the hero. */
  watermark: PartnerWatermark;
  /** Hand-drawn strike-through stroke (in the partner accent), served from /public. */
  scratchUrl: string;
  /** Body of the pre-filled tweet behind the hero share link. */
  shareText: string;
  /** Label of the hero share link. */
  shareCtaLabel: string;
}

export const ANSEM_PARTNER: DripPartner = {
  slug: "ansem",
  tokenSymbol: "ANSEM",
  tokenName: "Ansem",
  tokenMint: "9cRCn9rGT8V2imeM2BaKs13yhMEais3ruM3rPvTGpump",
  // Drop the ANSEM bull logo at /public/ansem.png to use it directly; otherwise
  // the icon is resolved from token metadata (see useTokenIcon).
  iconUrl: "/ansem.png",
  // Accent derived from the ANSEM "Black Bull" logo (neon-green horns/wordmark).
  accent: "#3FE24E",
  accentRgb: "63, 226, 78",
  heroTitle: "Drip",
  watermark: "bull",
  scratchUrl: "/scratch-ansem.svg",
  shareText:
    "I'm dripping $ANSEM on Kyros 🐂💧\n\nAuto-DCA my SOL yield straight into the Black Bull:",
  shareCtaLabel: "Want a boost? Ping Ansem about it! ↗",
};

export const ZCASH_PARTNER: DripPartner = {
  slug: "zcash",
  tokenSymbol: "ZEC",
  tokenName: "Zcash",
  tokenMint: "A7bdiYdS5GjqGFtxf17ppRHtDKPkkRqbKtR27dxvQXaS",
  iconUrl: "/zcash.png",
  // Official Zcash brand gold.
  accent: "#F4B728",
  accentRgb: "244, 183, 40",
  heroTitle: "Drip",
  // Spelled out in the nav, where a bare ticker next to "Stake"/"Loot" reads as noise.
  navLabel: "Zcash",
  watermark: "shield",
  scratchUrl: "/scratch-zcash.svg",
  shareText:
    "I'm dripping $ZEC on Kyros 🛡️💧\n\nAuto-DCA my SOL yield straight into Zcash:",
  shareCtaLabel: "Want a boost? Tell the Zcash community! ↗",
};

export const DRIP_PARTNERS: Record<string, DripPartner> = {
  [ANSEM_PARTNER.slug]: ANSEM_PARTNER,
  [ZCASH_PARTNER.slug]: ZCASH_PARTNER,
};

/** Resolves a router pathname (e.g. "/zcash") to its partner, if any. */
export const partnerForPathname = (pathname: string): DripPartner | undefined =>
  DRIP_PARTNERS[pathname.replace(/^\//, "")];

/**
 * The partner that brands the "Drip" nav item on every route. Only one can:
 * there is a single Drip item to brand. A route partner takes precedence on
 * its own page (see the Header).
 */
export const PINNED_NAV_PARTNER: DripPartner | undefined = Object.values(
  DRIP_PARTNERS
).find((partner) => partner.pinnedInNav);

// Bundled logo keyed by SPL mint, for partner reward tokens that ship a static
// asset. Preferred over on-chain/Jupiter metadata icons, which for meme tokens
// (e.g. ANSEM -> blackbullsol.com) often point at unreliable third-party URLs
// that don't serve an image.
export const PARTNER_ICON_BY_MINT: Record<string, string> = Object.fromEntries(
  Object.values(DRIP_PARTNERS)
    .filter((partner) => !!partner.iconUrl)
    .map((partner) => [partner.tokenMint, partner.iconUrl as string])
);
